import React, { useState } from 'react';

import CallIcon from '@mui/icons-material/Call';
import ChatIcon from '@mui/icons-material/Chat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MailIcon from '@mui/icons-material/Mail';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import {
  Avatar,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import CopyButton from './CopyButton/CopyButton';

type MLPersonaProps = {
  user?: PartialUser;
};

export type PartialUser = {
  ADSystemID?: string;
  DisplayName?: string;
  ImageUrlLarge?: string;
  ImageUrlMedium?: string;
  ImageUrlSmall?: string;
  WorkEmail?: string;
  DirectWorkPhone?: string;
  DisplayTitle?: string;
  PracticeGrpName?: string;
  PeopleOfficeAddress?: string;
  OfficeName?: string;
  PracticeGrpRollupName?: string;
};

const UserCard = ({ user }: MLPersonaProps) => {
  const aadTenantID = import.meta.env.VITE_AAD_TENANT_ID;
  return (
    <Grid container spacing={1} sx={{ maxWidth: '300px', alignItems: 'center' }}>
      <Grid size="auto">
        <Avatar alt={user?.DisplayName || ''} src={user?.ImageUrlLarge || ''} sx={{ width: 72, height: 72 }} />
      </Grid>
      <Grid size="grow" sx={{ flexGrow: 1 }}>
        <Grid container rowSpacing={0.25} columnSpacing={1}>
          <Grid size={12}>
            <Typography variant="h6" noWrap>
              {user?.DisplayName || ''}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" noWrap>
              {user?.DisplayTitle || ''}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" noWrap>
              {user?.PracticeGrpName || ''}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" noWrap>
              {user?.PracticeGrpRollupName || ''}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {user?.WorkEmail && (
        <Grid size={12}>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <IconButton
              color="inherit"
              aria-label="Mail"
              href={`mailto:${user?.WorkEmail}`}
              target="_blank"
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <MailIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="Chat"
              href={`https://teams.microsoft.com/l/chat/0/0?tenantId=${aadTenantID}&users=${user?.WorkEmail}`}
              target="_blank"
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <ChatIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="Call Video"
              href={`https://teams.microsoft.com/l/call/0/0?tenantId=${aadTenantID}&users=${user?.WorkEmail}&withVideo=true`}
              target="_blank"
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <VideoCameraFrontIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="Call"
              href={`https://teams.microsoft.com/l/call/0/0?tenantId=${aadTenantID}&users=${user?.WorkEmail}`}
              target="_blank"
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <CallIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Grid>
      )}
      {user?.ADSystemID && (
        <>
          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ textTransform: 'none', fontWeight: 'bold' }}>
              Contact information
            </Typography>
          </Grid>
          <Grid size={11}>
            <Button
              fullWidth
              color="inherit"
              aria-label="Mail"
              startIcon={<MailIcon fontSize="small" />}
              href={`mailto:${user?.WorkEmail}`}
              target="_blank"
              sx={{
                '&:hover': {
                  color: 'primary.main',
                },
                justifyContent: 'flex-start',
              }}
            >
              <Typography variant="body2" sx={{ textTransform: 'none' }} noWrap>
                {user?.WorkEmail || ''}
              </Typography>
            </Button>
          </Grid>
          <Grid size={1}>
            <CopyButton value={user?.WorkEmail || ''} />
          </Grid>
          {user?.DirectWorkPhone && (
            <>
              <Grid size={11}>
                <Button
                  fullWidth
                  color="inherit"
                  aria-label="Mail"
                  startIcon={<CallIcon fontSize="small" />}
                  href={`tel:${user?.DirectWorkPhone}`}
                  target="_blank"
                  sx={{
                    '&:hover': {
                      color: 'primary.main',
                    },
                    justifyContent: 'flex-start',
                  }}
                >
                  <Typography variant="body2" sx={{ textTransform: 'none' }} noWrap>
                    {user?.DirectWorkPhone || ''}
                  </Typography>
                </Button>
              </Grid>
              <Grid size={1}>
                <CopyButton value={user?.DirectWorkPhone || ''} />
              </Grid>
            </>
          )}
          {user?.OfficeName && (
            <>
              <Grid size={11}>
                <Button
                  fullWidth
                  color="inherit"
                  aria-label="Location"
                  startIcon={<LocationOnIcon fontSize="small" />}
                  sx={{
                    '&:hover': {
                      color: 'primary.main',
                    },
                    justifyContent: 'flex-start',
                  }}
                >
                  <Typography variant="body2" sx={{ textTransform: 'none' }} noWrap>
                    {`${user?.OfficeName} (${user?.PeopleOfficeAddress})`}
                  </Typography>
                </Button>
              </Grid>
              <Grid size={1}>
                <CopyButton value={user?.OfficeName || ''} />
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};

export const MLPersona = ({ user }: MLPersonaProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  return (
    <Tooltip title={user?.DisplayName || ''} placement="top">
      <div>
        <Avatar
          sx={{ width: 32, height: 32, cursor: 'pointer' }}
          alt={user?.DisplayName || ''}
          src={user?.ImageUrlSmall || ''}
          onClick={handleClick}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          {...{ disableRestoreFocus: true, closeAfterTransition: true }}
        >
          <Container tabIndex={-1} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <UserCard user={user} />
          </Container>
        </Popover>
      </div>
    </Tooltip>
  );
};
export default MLPersona;
